/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Button,
  Text,
  Flex,
  Spinner,
  Tooltip,
  Box,
  IconButton,
  ButtonGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Badge,
  HStack,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
// import { createSearchParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import {
  FunnelSimple,
  ListBullets,
  SquaresFour,
  FileText,
  X,
  CheckCircle,
  XCircle,
  ArrowUp,
  ArrowDown,
  Buildings,
  FilePdf,
  NotePencil,
} from 'phosphor-react';
import { CiCircleCheck } from 'react-icons/ci';
import { IoCloseCircleOutline } from 'react-icons/io5';

import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';
import { FormBox } from '../../../../components/formBox';
import SelectInputForm from '../../../../components/inputs/forms/SelectInputForm';
import MultiSelectInputForm from '../../../../components/inputs/forms/MultiSelectInputForm';
import { IData, IPagination, ISelect } from '../../../../types/shared';
import { formatCNPJ, getFormattedLabelString } from '../../../../utils/FormatFunctions';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { DateToolbarFilterImproved } from '../../../../components/DateToolbarFilterImproved';
import { PageBox } from '../../../../components/pageBox';
import { FilterDrawer } from '../../../../components/FilterDrawer';
import { useNfse, IRelatorioGeral, IRelatorioStatics } from '../../../../hooks/useNfse';
import { ICompany, useCompanies } from '../../../../hooks/useCompanies';
import ViewNotaModal from '../../../../components/viewNota';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';

type ViewMode = 'table' | 'grid';

const STATUS_OPTIONS = [
  { label: 'Todos', value: '' },
  { label: 'OK Valor Prestados', value: 'ok_valor_prestados' },
  { label: 'Erro Valor Prestados', value: 'nok_valor_prestados' },
  { label: 'OK Qnt. Notas Prestadas', value: 'ok_quantidade_prestados' },
  { label: 'Erro Qnt. Notas Prestadas', value: 'nok_quantidade_prestados' },
  { label: 'OK Valor Tomados', value: 'ok_valor_tomados' },
  { label: 'Erro Valor Tomados', value: 'nok_valor_tomados' },
  { label: 'OK Qnt. Notas Tomadas', value: 'ok_quantidade_tomados' },
  { label: 'Erro Qnt. Notas Tomadas', value: 'nok_quantidade_tomados' },
  { label: 'Auditado', value: 'com_anotacao' },
  { label: 'Com Relatório', value: 'com_relatorio' },
  { label: 'Sem Relatório', value: 'sem_relatorio' },
  { label: 'Empresas Homologadas', value: 'empresas_homologadas' },
  { label: 'Empresas Não Homologadas', value: 'empresas_nao_homologadas' },
];

export function ModulesHomeIntegrationNfseExplorerPage() {
  const { getRelatorioStatics, getRelatorioGeral, getPdfUrl } = useNfse();
  const { getCompanies, getCompaniesFilters } = useCompanies();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchMode, setSearchMode] = useState<'ID' | 'Nome' | 'CNPJ'>('ID');
  const searchModeRef = useRef(searchMode);
  const { filters, setFilters } = useMonthFilter();

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);
  const [loadingPdf, setLoadingPdf] = useState<string | null>(null);
  const [pdfModalUrl, setPdfModalUrl] = useState<string | null>(null);
  const [pdfModalTitle, setPdfModalTitle] = useState<string>('');
  const {
    isOpen: isPdfModalOpen,
    onOpen: onPdfModalOpen,
    onClose: onPdfModalClose,
  } = useDisclosure();

  // const [filters, setFilters] = useSearchParams(
  //   createSearchParams({
  //     page: '1',
  //     size: '50',
  //     date_start: moment().startOf('month').format('YYYY-MM-DD'),
  //     date_end: moment().endOf('month').format('YYYY-MM-DD'),
  //   }),
  // );

  // Verificar se há filtros ativos (exceto paginação, datas e campos de busca)
  const hasActiveFilters = useMemo(() => {
    const excludeKeys = ['page', 'size', 'date_start', 'date_end', 'search', 'cnpj', 'external_id'];
    return Array.from(filters.keys()).some(key => !excludeKeys.includes(key) && filters.get(key));
  }, [filters]);

  // Função para limpar todos os filtros
  const handleClearFilters = () => {
    const newFilters = new URLSearchParams();
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    newFilters.set(
      'date_start',
      moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    );
    newFilters.set('date_end', moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'));
    // Mantém campos de busca se existirem
    if (filters.get('search')) newFilters.set('search', filters.get('search') || '');
    if (filters.get('cnpj')) newFilters.set('cnpj', filters.get('cnpj') || '');
    if (filters.get('external_id')) newFilters.set('external_id', filters.get('external_id') || '');
    setFilters(newFilters);
    filtersFormMethods.reset();
  };

  // Aplicar filtro clicando nos cards
  const handleCardFilter = (filterKey: string, filterValue: string) => {
    const newFilters = new URLSearchParams(Object.fromEntries(filters));

    // Se já está com esse filtro, remove
    if (filters.get(filterKey) === filterValue) {
      newFilters.delete(filterKey);
    } else {
      newFilters.set('page', '1');
      newFilters.set(filterKey, filterValue);
    }

    setFilters(newFilters);
  };

  const filtersFormMethods = useForm<{
    search: string;
    cnpj: string;
    status: ISelect<string>;
    empresa: ICompany;
    cidade: ISelect<string>;
    uf: ISelect<string>;
    grupo_de_empresas: ISelect<string>[];
    fiscal: ISelect<string>;
    contabil: ISelect<string>;
    tags: ISelect<string>;
    comercial: ISelect<string>;
    financeiro: ISelect<string>;
    contas_a_pagar: ISelect<string>;
    encerramento_anual: ISelect<string>;
    imposto_de_renda: ISelect<string>;
    integracao_contabil: ISelect<string>;
    integracao_fiscal: ISelect<string>;
    pessoal: ISelect<string>;
    relacionamento_com_cliente: ISelect<string>;
    societario: ISelect<string>;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: '',
      cnpj: filters.get('cnpj') ?? '',
    },
  });

  // Debounce para o campo de busca com modo selecionável
  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    const newFilters = new URLSearchParams(Object.fromEntries(filters));
    const currentMode = searchModeRef.current;

    // Remove filtros anteriores de busca
    newFilters.delete('search');
    newFilters.delete('cnpj');
    newFilters.delete('external_id');

    if (searchValue) {
      switch (currentMode) {
        case 'ID':
          newFilters.set('external_id', searchValue);
          break;
        case 'Nome':
          newFilters.set('search', searchValue);
          break;
        case 'CNPJ':
          newFilters.set('cnpj', searchValue.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
          break;
        default:
          break;
      }
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  // Debounce para os campos do FilterDrawer
  const debouncedDrawerFields = useDebounceCallback(values => {
    const newFilters = new URLSearchParams(Object.fromEntries(filters));
    newFilters.set('page', '1');

    // Atualiza status apenas se foi explicitamente alterado no form
    if (values.status !== undefined) {
      if (values.status?.value) {
        newFilters.set('status', values.status.value);
      } else if (values.status === null) {
        newFilters.delete('status');
      }
    }

    // Atualiza empresa apenas se foi explicitamente alterado
    if (values.empresa !== undefined) {
      if (values.empresa?.id) {
        newFilters.set('id_emp', String(values.empresa.id));
      } else if (values.empresa === null) {
        newFilters.delete('id_emp');
      }
    }

    // Campos do FilterDrawer - só atualiza se foi alterado
    const drawerFields = [
      'cidade',
      'uf',
      'fiscal',
      'contabil',
      'tags',
      'comercial',
      'financeiro',
      'contas_a_pagar',
      'encerramento_anual',
      'imposto_de_renda',
      'integracao_contabil',
      'integracao_fiscal',
      'pessoal',
      'relacionamento_com_cliente',
      'societario',
    ];

    drawerFields.forEach(field => {
      if (values[field] !== undefined) {
        if (values[field]?.value) {
          newFilters.set(field, values[field].value);
        } else if (values[field] === null) {
          newFilters.delete(field);
        }
      }
    });

    // Tratamento especial para grupo_de_empresas (multi-select)
    if (values.grupo_de_empresas !== undefined) {
      if (Array.isArray(values.grupo_de_empresas) && values.grupo_de_empresas.length > 0) {
        const gruposValues = values.grupo_de_empresas.map((g: any) => g.value).join(',');
        newFilters.set('grupo_de_empresas', gruposValues);
      } else {
        newFilters.delete('grupo_de_empresas');
      }
    }

    setFilters(newFilters);
  }, 500);

  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') {
        debouncedSearch(values.search || '');
      } else if (name) {
        debouncedDrawerFields(values);
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch, debouncedSearch, debouncedDrawerFields]);

  // Re-executa a busca quando o modo de pesquisa muda
  useEffect(() => {
    debouncedSearch(filtersFormMethods.getValues().search || '');
  }, [searchMode]);

  // Query para lista de empresas (para o select)
  const { data: dataCompaniesSelect, isFetching: isFetchingCompanySelect } = useQuery<
    IData<ICompany[]>
  >({
    queryKey: ['CompaniesSelect', { page: 1, size: 30, search: '' }],
    queryFn: getCompanies,
  });

  const { items: companies } = dataCompaniesSelect?.data ?? {
    items: [],
    ...({} as IPagination),
  };

  // Query para opções de filtros
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: ['CompaniesFilters'],
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  // Query para estatísticas (statics) - envia todos os filtros
  const { data: staticsData, isFetching: isFetchingStatics } = useQuery<IRelatorioStatics>({
    queryKey: ['RelatorioStatics', cleanFilters(Object.fromEntries([...Array.from(filters)]))],
    queryFn: getRelatorioStatics,
  });

  // Query para lista geral
  const { data: relatorioGeralData, isFetching: isFetchingRelatorioGeral } = useQuery({
    queryKey: ['RelatorioGeral', cleanFilters(Object.fromEntries([...Array.from(filters)]))],
    queryFn: getRelatorioGeral,
  });

  const relatorioGeralList = relatorioGeralData?.items ?? [];
  const pagination = relatorioGeralData
    ? {
        page: relatorioGeralData.page,
        pages: relatorioGeralData.pages,
        size: relatorioGeralData.size,
        total: relatorioGeralData.total,
      }
    : null;

  // Função para calcular status de comparação de uma empresa (apenas Prestados por enquanto)
  // Cenários (mesma regra para quantidade e valor):
  // | Relatório: 0, Domínio: null        | OK (0 == 0)              |
  // | Relatório: null, Domínio: 0        | Sem relatório (cinza)    |
  // | Relatório: null, Domínio: 14       | NOK (vermelho - deveria ter relatório) |
  // | Relatório: 4, Domínio: 0           | NOK/Divergência (4 != 0) |
  // | Relatório: 0, Domínio: 0           | OK (0 == 0)              |
  // | Relatório: null, Domínio: null     | Sem relatório (cinza)    |
  const getComparisonStatus = (item: IRelatorioGeral) => {
    const relatorioQnt = item.total_notas_prestadas;
    const dominioQnt = item.qt_nfse_dominio_saidas;
    const relatorioValor = item.total_valor_prestados;
    const dominioValor = item.vl_servico;

    const dominioQntValue = dominioQnt ?? 0;
    const dominioValorValue = dominioValor ?? 0;

    // Se relatório (quantidade OU valor) é null
    if (relatorioQnt === null || relatorioValor === null) {
      // Se Domínio também é 0 (ou null), é sem_relatorio (cinza)
      // Se Domínio tem valor > 0, é divergência (vermelho)
      if (dominioQntValue > 0 || dominioValorValue > 0) {
        return 'nok_prestados';
      }
      return 'sem_relatorio';
    }

    // Relatório tem valor (incluindo 0), compara com domínio
    const okQnt = relatorioQnt === dominioQntValue;
    const okValor = relatorioValor === dominioValorValue;

    return okQnt && okValor ? 'ok' : 'nok_prestados';
  };

  // Renderiza o símbolo de status (apenas Prestados por enquanto)
  const renderStatusSymbol = (status: string) => {
    switch (status) {
      case 'ok':
        return (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
            <CheckCircle size={16} color="#16A34A" weight="fill" />
          </div>
        );
      case 'nok_prestados':
        return (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
            <XCircle size={16} color="#DC2626" weight="fill" />
          </div>
        );
      case 'sem_relatorio':
        return (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
            <Text fontSize="10px" color="gray.500" fontWeight="bold">
              -
            </Text>
          </div>
        );
      default:
        return null;
    }
  };

  // Renderização dos Cards de Totalizadores
  const renderDashboardCards = () => {
    if (isFetchingStatics) {
      return (
        <div className="flex items-center justify-center py-4">
          <Spinner size="lg" color="blue.500" />
          <Text ml={3}>Carregando estatísticas...</Text>
        </div>
      );
    }

    if (!staticsData) return null;

    return (
      <div className="w-full">
        {/* Cards principais de estatísticas */}
        <div className="grid grid-cols-2 gap-2 pb-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {/* Card Total Empresas */}
          <Flex
            className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
              !filters.get('status') ? 'bg-[#E2E6FF]' : 'bg-body'
            }`}
            onClick={() => handleClearFilters()}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DBEAFE] p-2">
              <Buildings color="#3B82F6" className="size-5" />
            </div>
            <div className="flex w-full flex-col">
              <Text fontSize="11px" color="#718EBF">
                Total Empresas
              </Text>
              <Text fontSize="16px" fontWeight="bold">
                {staticsData.total_empresas?.toLocaleString() ?? 0}
              </Text>
            </div>
          </Flex>

          {/* Card Com Relatório */}
          <Tooltip
            label="Empresas que possuem relatório no período (% em relação ao total de empresas)"
            placement="top"
          >
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'com_relatorio' ? 'bg-[#DCFCE7]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'com_relatorio')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCFCE7] p-2">
                <CheckCircle color="#16A34A" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  Com Relatório
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.empresas_com_relatorio?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.empresas_com_relatorio_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card Sem Relatório */}
          <Tooltip
            label="Empresas que não possuem relatório no período (% em relação ao total de empresas)"
            placement="top"
          >
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'sem_relatorio' ? 'bg-[#FFE0EB]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'sem_relatorio')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE0EB] p-2">
                <XCircle color="#DC2626" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  Sem Relatório
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.empresas_sem_relatorio?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.empresas_sem_relatorio_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card Homologadas */}
          <Tooltip label="Empresas com relatório em cidade homologada" placement="top">
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'empresas_homologadas' ? 'bg-[#E9D5FF]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'empresas_homologadas')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E9D5FF] p-2">
                <CheckCircle color="#9333EA" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  Homologadas
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.empresas_homologadas?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.empresas_homologadas_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card Não Homologadas */}
          <Tooltip label="Empresas com relatório mas sem cidade homologada" placement="top">
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'empresas_nao_homologadas' ? 'bg-[#FFF3CD]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'empresas_nao_homologadas')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF3CD] p-2">
                <XCircle color="#EA580C" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  Não Homologadas
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.empresas_nao_homologadas?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.empresas_nao_homologadas_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card Auditado */}
          <Tooltip
            label="Empresas auditadas (com anotação marcada como 'Auditado')"
            placement="top"
          >
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'com_anotacao' ? 'bg-gray-200' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'com_anotacao')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 p-2">
                <NotePencil color="#6B7280" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  Auditado
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.com_anotacao?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.com_anotacao_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card OK Valor Prestados */}
          <Tooltip label="Empresas onde o valor das notas do Relatório = Domínio" placement="top">
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'ok_valor_prestados' ? 'bg-[#DCFAF8]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'ok_valor_prestados')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCFAF8] p-2">
                <CiCircleCheck color="#16DBCC" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  OK Vl. Prestados
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.ok_valor_prestados?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.ok_valor_prestados_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card OK Qnt. Prestadas */}
          <Tooltip
            label="Empresas onde a quantidade de notas prestadas do Relatório = Domínio"
            placement="top"
          >
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'ok_quantidade_prestados' ? 'bg-[#DCFCE7]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'ok_quantidade_prestados')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCFCE7] p-2">
                <ArrowUp color="#16A34A" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  OK Qt. Prestadas
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.ok_quantidade_prestados?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.ok_quantidade_prestados_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card OK Valor Tomados */}
          <Tooltip
            label="Empresas onde o valor das notas tomadas do Relatório = Domínio"
            placement="top"
          >
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'ok_valor_tomados' ? 'bg-[#DCFAF8]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'ok_valor_tomados')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCFAF8] p-2">
                <CiCircleCheck color="#16DBCC" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  OK Vl. Tomados
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.ok_valor_tomados?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.ok_valor_tomados_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card OK Qnt. Tomadas */}
          <Tooltip
            label="Empresas onde a quantidade de notas tomadas do Relatório = Domínio"
            placement="top"
          >
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'ok_quantidade_tomados' ? 'bg-[#DCFCE7]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'ok_quantidade_tomados')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCFCE7] p-2">
                <ArrowDown color="#16A34A" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  OK Qt. Tomadas
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.ok_quantidade_tomados?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.ok_quantidade_tomados_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card Erro Valor Prestados */}
          <Tooltip
            label="Empresas com divergência de valor entre Relatório e Domínio"
            placement="top"
          >
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'nok_valor_prestados' ? 'bg-[#FFE0EB]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'nok_valor_prestados')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE0EB] p-2">
                <IoCloseCircleOutline color="#FF82AC" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  Erro Vl. Prestados
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.nok_valor_prestados?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.nok_valor_prestados_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card Erro Qnt. Prestadas */}
          <Tooltip
            label="Empresas com divergência de quantidade de notas prestadas"
            placement="top"
          >
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'nok_quantidade_prestados' ? 'bg-[#FFE0EB]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'nok_quantidade_prestados')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE0EB] p-2">
                <IoCloseCircleOutline color="#FF82AC" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  Erro Qt. Prestadas
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.nok_quantidade_prestados?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.nok_quantidade_prestados_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card Erro Valor Tomados */}
          <Tooltip label="Empresas com divergência de valor de notas tomadas" placement="top">
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'nok_valor_tomados' ? 'bg-[#FFE0EB]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'nok_valor_tomados')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE0EB] p-2">
                <IoCloseCircleOutline color="#FF82AC" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  Erro Vl. Tomados
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.nok_valor_tomados?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.nok_valor_tomados_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>

          {/* Card Erro Qnt. Tomadas */}
          <Tooltip label="Empresas com divergência de quantidade de notas tomadas" placement="top">
            <Flex
              className={`flex cursor-pointer items-center gap-2 rounded-xl p-3 ${
                filters.get('status') === 'nok_quantidade_tomados' ? 'bg-[#FFE0EB]' : 'bg-body'
              }`}
              onClick={() => handleCardFilter('status', 'nok_quantidade_tomados')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE0EB] p-2">
                <IoCloseCircleOutline color="#FF82AC" className="size-5" />
              </div>
              <div className="flex w-full flex-col">
                <Text fontSize="11px" color="#718EBF">
                  Erro Qt. Tomadas
                </Text>
                <Text fontSize="16px" fontWeight="bold">
                  {staticsData.nok_quantidade_tomados?.toLocaleString() ?? 0}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {staticsData.nok_quantidade_tomados_pct?.toFixed(1) ?? 0}%
                </Text>
              </div>
            </Flex>
          </Tooltip>
        </div>
      </div>
    );
  };

  // Função para formatar valores para moeda
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Função para abrir PDF com URL assinada do S3 em modal
  const handleOpenPdf = async (pdfKey: string, pdfId: string, title: string) => {
    try {
      setLoadingPdf(pdfId);
      const signedUrl = await getPdfUrl(pdfKey);
      setPdfModalUrl(signedUrl);
      setPdfModalTitle(title);
      onPdfModalOpen();
    } catch {
      // Erro silencioso ao obter URL do PDF
    } finally {
      setLoadingPdf(null);
    }
  };

  // Função para fechar o modal de PDF
  const handleClosePdfModal = () => {
    onPdfModalClose();
    setPdfModalUrl(null);
    setPdfModalTitle('');
  };

  // Renderização do Grid (Cards por empresa)
  const renderGridView = () => (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {relatorioGeralList.map((item: IRelatorioGeral) => {
        const status = getComparisonStatus(item);
        const relatorioQnt = item.total_notas_prestadas;
        const dominioQnt = item.qt_nfse_dominio_saidas;
        const relatorioValor = item.total_valor_prestados;
        const dominioValor = item.vl_servico;

        // Lógica baseada na tabela de cenários (mesma regra para quantidade e valor):
        // | Relatório: 0, Domínio: null        | OK (0 == 0)              |
        // | Relatório: null, Domínio: 0        | Sem relatório (cinza)    |
        // | Relatório: null, Domínio: 14       | NOK (vermelho)           |
        // | Relatório: 4, Domínio: 0           | NOK/Divergência (4 != 0) |
        // | Relatório: 0, Domínio: 0           | OK (0 == 0)              |
        // | Relatório: null, Domínio: null     | Sem relatório (cinza)    |
        const dominioQntValue = dominioQnt ?? 0;
        const dominioValorValue = dominioValor ?? 0;
        const relatorioIsNull = relatorioQnt === null || relatorioValor === null;

        // Se relatório é null mas domínio tem valor > 0, é divergência (vermelho)
        const semRelatorio = relatorioIsNull && dominioQntValue === 0 && dominioValorValue === 0;
        const okQnt = !relatorioIsNull && relatorioQnt === dominioQntValue;
        const okValor = !relatorioIsNull && relatorioValor === dominioValorValue;
        const okPrestados = okQnt && okValor;

        // Variáveis para Tomados
        const relatorioQntTomados = item.total_notas_tomadas;
        const dominioQntTomados = item.qt_nfse_dominio_entradas;
        const relatorioValorTomados = item.total_valor_tomados;
        const dominioValorTomados = item.vl_nfse_dominio_entradas;

        const dominioQntTomadosValue = dominioQntTomados ?? 0;
        const dominioValorTomadosValue = dominioValorTomados ?? 0;
        const relatorioTomadosIsNull =
          relatorioQntTomados === null || relatorioValorTomados === null;

        const semRelatorioTomados =
          relatorioTomadosIsNull && dominioQntTomadosValue === 0 && dominioValorTomadosValue === 0;
        const okQntTomados =
          !relatorioTomadosIsNull && relatorioQntTomados === dominioQntTomadosValue;
        const okValorTomados =
          !relatorioTomadosIsNull && relatorioValorTomados === dominioValorTomadosValue;
        const okTomados = okQntTomados && okValorTomados;

        // Determina as cores baseado no status para Tomados
        const getTomadosCardStyle = () => {
          if (semRelatorioTomados) return 'border-gray-200 bg-gray-50';
          return okTomados ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50';
        };

        const getTomadosIconColor = () => {
          if (semRelatorioTomados) return '#9CA3AF';
          return okTomados ? '#16A34A' : '#DC2626';
        };

        const getTomadosTextColor = () => {
          if (semRelatorioTomados) return 'gray.500';
          return okTomados ? 'green.600' : 'red.600';
        };

        // Determina as cores baseado no status
        const getCardStyle = () => {
          if (semRelatorio) return 'border-gray-200 bg-gray-50';
          return okPrestados ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50';
        };

        const getIconColor = () => {
          if (semRelatorio) return '#9CA3AF';
          return okPrestados ? '#16A34A' : '#DC2626';
        };

        const getTextColor = () => {
          if (semRelatorio) return 'gray.500';
          return okPrestados ? 'green.600' : 'red.600';
        };

        return (
          <div
            key={item.id_emp}
            className="relative rounded-lg border border-gray-300 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
          >
            {/* Símbolo de Status no canto esquerdo */}
            <div className="absolute left-2 top-2">{renderStatusSymbol(status)}</div>

            {/* Botão de Anotação no canto direito */}
            <div className="absolute right-2 top-2">
              <Tooltip
                label={
                  item.qtd_anotacoes
                    ? `Ver ${item.qtd_anotacoes} anotação(ões)`
                    : 'Adicionar anotação'
                }
                placement="left"
              >
                <Box position="relative">
                  <ViewNotaModal
                    screen="nfseDashboard"
                    id={item.id_emp}
                    dateStart={filters.get('date_start')}
                    dateEnd={filters.get('date_end')}
                    defaultTab="nfseDashboard"
                    companyName={item.razao_social}
                  >
                    <IconButton
                      aria-label="Ver anotacoes"
                      icon={<NotePencil size={16} />}
                      size="xs"
                      variant="ghost"
                      colorScheme={item.qtd_anotacoes ? 'blue' : 'gray'}
                    />
                  </ViewNotaModal>
                  {item.qtd_anotacoes ? (
                    <Badge
                      position="absolute"
                      top="-1"
                      right="-1"
                      fontSize="9px"
                      colorScheme="blue"
                      borderRadius="full"
                      minW="16px"
                      h="16px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      pointerEvents="none"
                    >
                      {item.qtd_anotacoes}
                    </Badge>
                  ) : null}
                </Box>
              </Tooltip>
            </div>

            <div className="flex w-full flex-col items-center gap-2">
              {/* Header: ID + Nome + CNPJ */}
              <div className="flex w-full flex-col items-center gap-1">
                <Text fontSize="12px" color="gray.500" fontWeight="semibold">
                  ID: {item.codi_emp}
                </Text>
                <div className="flex w-full flex-col items-center gap-0.5 pr-8">
                  <Tooltip label={item.razao_social} placement="top">
                    <Text
                      fontWeight="semibold"
                      fontSize="xs"
                      lineHeight="1.2"
                      noOfLines={1}
                      textAlign="center"
                    >
                      {item.razao_social}
                    </Text>
                  </Tooltip>
                  <Text fontSize="xs" color="gray.600">
                    {formatCNPJ(item.cnpj)}
                  </Text>
                </div>
              </div>

              {/* Cards de Comparação */}
              <div className="flex w-full flex-col gap-3 md:flex-row md:justify-center xl:flex-col 2xl:flex-row 2xl:justify-center">
                {/* Prestados - Comparação ativa */}
                <Tooltip label="Notas Prestadas: Relatório vs Domínio (Saídas)" placement="top">
                  <div className={`flex-1 rounded-lg border p-3 ${getCardStyle()}`}>
                    <div className="mb-2 flex items-center justify-center gap-1">
                      <ArrowUp size={14} className="flex-shrink-0" color={getIconColor()} />
                      <Text fontSize="sm" fontWeight="bold" color={getTextColor()}>
                        Notas Prestadas
                      </Text>
                      {!semRelatorio &&
                        (okPrestados ? (
                          <CheckCircle
                            size={14}
                            className="flex-shrink-0"
                            color="#16A34A"
                            weight="fill"
                          />
                        ) : (
                          <XCircle
                            size={14}
                            className="flex-shrink-0"
                            color="#DC2626"
                            weight="fill"
                          />
                        ))}
                    </div>
                    <div className="flex justify-center gap-4 text-[12px]">
                      <div className="text-center">
                        <Text color="gray.800" fontWeight="medium" mb={1} fontSize="12px">
                          Relatório
                        </Text>
                        <Text fontSize="11px" color="gray.600">
                          Qnt. Notas:
                        </Text>
                        <Text fontWeight="semibold" fontSize="14px" mb={1}>
                          {item.total_notas_prestadas !== null ? item.total_notas_prestadas : '-'}
                        </Text>
                        <Text fontSize="11px" color="gray.600">
                          Valor:
                        </Text>
                        <Text fontSize="14px" fontWeight="semibold" color="gray.800">
                          {item.total_valor_prestados !== null
                            ? formatCurrency(item.total_valor_prestados)
                            : '-'}
                        </Text>
                      </div>
                      <div className="text-center">
                        <Text color="gray.800" fontWeight="medium" mb={1} fontSize="12px">
                          Domínio
                        </Text>
                        <Text fontSize="11px" color="gray.600">
                          Qnt. Notas:
                        </Text>
                        <Text fontWeight="semibold" fontSize="14px" mb={1}>
                          {item.qt_nfse_dominio_saidas !== null ? item.qt_nfse_dominio_saidas : '-'}
                        </Text>
                        <Text fontSize="11px" color="gray.600">
                          Valor:
                        </Text>
                        <Text fontSize="14px" fontWeight="semibold" color="gray.800">
                          {item.vl_servico !== null ? formatCurrency(item.vl_servico) : '-'}
                        </Text>
                      </div>
                    </div>
                    {!semRelatorio && !okPrestados && (
                      <div className="mt-2 flex items-center justify-center gap-1 rounded bg-red-100 px-2 py-1">
                        <XCircle size={12} color="#DC2626" weight="fill" />
                        <Text fontSize="10px" color="red.600" fontWeight="medium">
                          Diferença:{' '}
                          {Math.abs(
                            (item.total_notas_prestadas ?? 0) - (item.qt_nfse_dominio_saidas ?? 0),
                          )}
                        </Text>
                      </div>
                    )}
                  </div>
                </Tooltip>

                {/* Tomados - Comparação ativa */}
                <Tooltip label="Notas Tomadas: Relatório vs Domínio (Entradas)" placement="top">
                  <div className={`flex-1 rounded-lg border p-3 ${getTomadosCardStyle()}`}>
                    <div className="mb-2 flex items-center justify-center gap-1">
                      <ArrowDown
                        size={14}
                        className="flex-shrink-0"
                        color={getTomadosIconColor()}
                      />
                      <Text fontSize="sm" fontWeight="bold" color={getTomadosTextColor()}>
                        Notas Tomadas
                      </Text>
                      {!semRelatorioTomados &&
                        (okTomados ? (
                          <CheckCircle
                            size={14}
                            className="flex-shrink-0"
                            color="#16A34A"
                            weight="fill"
                          />
                        ) : (
                          <XCircle
                            size={14}
                            className="flex-shrink-0"
                            color="#DC2626"
                            weight="fill"
                          />
                        ))}
                    </div>
                    <div className="flex justify-center gap-4 text-[12px]">
                      <div className="text-center">
                        <Text color="gray.800" fontWeight="medium" mb={1} fontSize="12px">
                          Relatório
                        </Text>
                        <Text fontSize="11px" color="gray.600">
                          Qnt. Notas:
                        </Text>
                        <Text fontWeight="semibold" fontSize="14px" mb={1}>
                          {item.total_notas_tomadas !== null ? item.total_notas_tomadas : '-'}
                        </Text>
                        <Text fontSize="11px" color="gray.600">
                          Valor:
                        </Text>
                        <Text fontSize="14px" fontWeight="semibold" color="gray.800">
                          {item.total_valor_tomados !== null
                            ? formatCurrency(item.total_valor_tomados)
                            : '-'}
                        </Text>
                      </div>
                      <div className="text-center">
                        <Text color="gray.800" fontWeight="medium" mb={1} fontSize="12px">
                          Domínio
                        </Text>
                        <Text fontSize="11px" color="gray.600">
                          Qnt. Notas:
                        </Text>
                        <Text fontWeight="semibold" fontSize="14px" mb={1}>
                          {item.qt_nfse_dominio_entradas !== null
                            ? item.qt_nfse_dominio_entradas
                            : '-'}
                        </Text>
                        <Text fontSize="11px" color="gray.600">
                          Valor:
                        </Text>
                        <Text fontSize="14px" fontWeight="semibold" color="gray.800">
                          {item.vl_nfse_dominio_entradas != null
                            ? formatCurrency(item.vl_nfse_dominio_entradas)
                            : '-'}
                        </Text>
                      </div>
                    </div>
                    {!semRelatorioTomados && !okTomados && (
                      <div className="mt-2 flex items-center justify-center gap-1 rounded bg-red-100 px-2 py-1">
                        <XCircle size={12} color="#DC2626" weight="fill" />
                        <Text fontSize="10px" color="red.600" fontWeight="medium">
                          Diferença:{' '}
                          {Math.abs(
                            (item.total_notas_tomadas ?? 0) - (item.qt_nfse_dominio_entradas ?? 0),
                          )}
                        </Text>
                      </div>
                    )}
                  </div>
                </Tooltip>
              </div>

              {/* Rodapé do card: Cidade/Estado + Botões PDF */}
              <div className="mt-2 flex w-full items-center justify-between">
                {/* Botão PDF Prestados - Esquerda */}
                {item.pdf_path_prestados ? (
                  <Tooltip label="Abrir PDF Prestados" placement="top">
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="green"
                      leftIcon={
                        loadingPdf === `${item.id_emp}-prestados` ? (
                          <Spinner size="xs" />
                        ) : (
                          <FilePdf size={14} />
                        )
                      }
                      onClick={() =>
                        handleOpenPdf(
                          item.pdf_path_prestados!,
                          `${item.id_emp}-prestados`,
                          `PDF Prestados - ${item.razao_social}`,
                        )
                      }
                      isDisabled={loadingPdf === `${item.id_emp}-prestados`}
                      opacity={0.8}
                      _hover={{ opacity: 1 }}
                    >
                      <Text fontSize="10px">Prestados</Text>
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip label="PDF Prestados não disponível" placement="top">
                    <div className="flex items-center gap-1 px-2 py-1 opacity-50">
                      <FilePdf size={14} color="#9CA3AF" />
                      <Text fontSize="10px" color="gray.400">
                        -
                      </Text>
                    </div>
                  </Tooltip>
                )}

                {/* Cidade/Estado - Centro */}
                {item.cidade_estado && (
                  <Text fontSize="10px" color="gray.400" textAlign="center">
                    {item.cidade_estado}
                  </Text>
                )}

                {/* Botão PDF Tomados - Direita */}
                {item.pdf_path_tomados ? (
                  <Tooltip label="Abrir PDF Tomados" placement="top">
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      leftIcon={
                        loadingPdf === `${item.id_emp}-tomados` ? (
                          <Spinner size="xs" />
                        ) : (
                          <FilePdf size={14} />
                        )
                      }
                      onClick={() =>
                        handleOpenPdf(
                          item.pdf_path_tomados!,
                          `${item.id_emp}-tomados`,
                          `PDF Tomados - ${item.razao_social}`,
                        )
                      }
                      isDisabled={loadingPdf === `${item.id_emp}-tomados`}
                      opacity={0.8}
                      _hover={{ opacity: 1 }}
                    >
                      <Text fontSize="10px">Tomados</Text>
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip label="PDF Tomados não disponível" placement="top">
                    <div className="flex items-center gap-1 px-2 py-1 opacity-50">
                      <FilePdf size={14} color="#9CA3AF" />
                      <Text fontSize="10px" color="gray.400">
                        -
                      </Text>
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Renderização da Tabela
  const renderTableView = () => (
    <Box overflowX="auto">
      <table className="w-full min-w-[700px] text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="whitespace-nowrap px-2 py-2 text-left font-medium text-gray-600 md:px-3">
              Empresa
            </th>
            <th className="hidden whitespace-nowrap px-2 py-2 text-left font-medium text-gray-600 sm:table-cell md:px-3">
              CNPJ
            </th>
            <th
              className="whitespace-nowrap px-2 py-2 text-center font-medium text-gray-600"
              colSpan={2}
            >
              <div className="flex flex-col items-center">
                <Text fontSize="xs">Notas Prestadas</Text>
                <div className="flex gap-2 text-[10px] md:gap-4">
                  <Text color="gray.400">Relatório</Text>
                  <Text color="gray.400">Domínio</Text>
                </div>
              </div>
            </th>
            <th
              className="whitespace-nowrap px-2 py-2 text-center font-medium text-gray-600"
              colSpan={2}
            >
              <div className="flex flex-col items-center">
                <Text fontSize="xs">Notas Tomadas</Text>
                <div className="flex gap-2 text-[10px] md:gap-4">
                  <Text color="gray.400">Relatório</Text>
                  <Text color="gray.400">Domínio</Text>
                </div>
              </div>
            </th>
            <th className="whitespace-nowrap px-2 py-2 text-center font-medium text-gray-600">
              Status
            </th>
            <th className="whitespace-nowrap px-2 py-2 text-center font-medium text-gray-600">
              Acoes
            </th>
          </tr>
        </thead>
        <tbody>
          {relatorioGeralList.map((item: IRelatorioGeral) => {
            const status = getComparisonStatus(item);
            const okPrestados = item.total_notas_prestadas === item.qt_nfse_dominio_saidas;
            const okTomados = item.total_notas_tomadas === item.qt_nfse_dominio_entradas;

            return (
              <tr key={item.id_emp} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="max-w-[120px] px-2 py-2 md:max-w-[200px] md:px-3">
                  <Tooltip label={item.razao_social}>
                    <Text noOfLines={1} fontSize="xs">
                      {item.razao_social}
                    </Text>
                  </Tooltip>
                </td>
                <td className="hidden whitespace-nowrap px-2 py-2 text-gray-600 sm:table-cell md:px-3">
                  <Text fontSize="xs">{formatCNPJ(item.cnpj)}</Text>
                </td>
                {/* Prestados - Relatório */}
                <td
                  className={`px-1 py-2 text-center md:px-2 ${okPrestados ? 'bg-green-50' : 'bg-red-50'}`}
                >
                  <div className="flex flex-col items-center">
                    <Text
                      fontWeight="semibold"
                      fontSize="xs"
                      color={okPrestados ? 'green.600' : 'red.600'}
                    >
                      {item.total_notas_prestadas ?? 0}
                    </Text>
                    <Text fontSize="9px" color="gray.500" noOfLines={1}>
                      {formatCurrency(item.total_valor_prestados ?? 0)}
                    </Text>
                  </div>
                </td>
                {/* Prestados - Domínio */}
                <td
                  className={`px-1 py-2 text-center md:px-2 ${okPrestados ? 'bg-green-50' : 'bg-red-50'}`}
                >
                  <div className="flex flex-col items-center">
                    <div className="flex items-center gap-1">
                      <Text
                        fontWeight="semibold"
                        fontSize="xs"
                        color={okPrestados ? 'green.600' : 'red.600'}
                      >
                        {item.qt_nfse_dominio_saidas ?? 0}
                      </Text>
                      {okPrestados ? (
                        <CheckCircle size={12} color="#16A34A" />
                      ) : (
                        <XCircle size={12} color="#DC2626" />
                      )}
                    </div>
                    <Text fontSize="9px" color="gray.500" noOfLines={1}>
                      {formatCurrency(item.vl_servico ?? 0)}
                    </Text>
                  </div>
                </td>
                {/* Tomados - Relatório */}
                <td
                  className={`px-1 py-2 text-center md:px-2 ${okTomados ? 'bg-green-50' : 'bg-red-50'}`}
                >
                  <div className="flex flex-col items-center">
                    <Text
                      fontWeight="semibold"
                      fontSize="xs"
                      color={okTomados ? 'green.600' : 'red.600'}
                    >
                      {item.total_notas_tomadas ?? 0}
                    </Text>
                    <Text fontSize="9px" color="gray.500" noOfLines={1}>
                      {formatCurrency(item.total_valor_tomados ?? 0)}
                    </Text>
                  </div>
                </td>
                {/* Tomados - Domínio */}
                <td
                  className={`px-1 py-2 text-center md:px-2 ${okTomados ? 'bg-green-50' : 'bg-red-50'}`}
                >
                  <div className="flex flex-col items-center">
                    <div className="flex items-center gap-1">
                      <Text
                        fontWeight="semibold"
                        fontSize="xs"
                        color={okTomados ? 'green.600' : 'red.600'}
                      >
                        {item.qt_nfse_dominio_entradas ?? 0}
                      </Text>
                      {okTomados ? (
                        <CheckCircle size={12} color="#16A34A" />
                      ) : (
                        <XCircle size={12} color="#DC2626" />
                      )}
                    </div>
                    <Text fontSize="9px" color="gray.500" noOfLines={1}>
                      {item.vl_nfse_dominio_entradas != null
                        ? formatCurrency(item.vl_nfse_dominio_entradas)
                        : '-'}
                    </Text>
                  </div>
                </td>
                <td className="px-2 py-2 text-center md:px-3">{renderStatusSymbol(status)}</td>
                <td className="px-2 py-2 text-center md:px-3">
                  <Tooltip
                    label={
                      item.qtd_anotacoes
                        ? `Ver ${item.qtd_anotacoes} anotação(ões)`
                        : 'Adicionar anotação'
                    }
                    placement="top"
                  >
                    <Box display="inline-block" position="relative">
                      <ViewNotaModal
                        screen="nfseDashboard"
                        id={item.id_emp}
                        dateStart={filters.get('date_start')}
                        dateEnd={filters.get('date_end')}
                        defaultTab="nfseDashboard"
                        companyName={item.razao_social}
                      >
                        <IconButton
                          aria-label="Ver anotacoes"
                          icon={<NotePencil size={16} />}
                          size="xs"
                          variant="ghost"
                          colorScheme={item.qtd_anotacoes ? 'blue' : 'gray'}
                        />
                      </ViewNotaModal>
                      {item.qtd_anotacoes ? (
                        <Badge
                          position="absolute"
                          top="-1"
                          right="-1"
                          fontSize="9px"
                          colorScheme="blue"
                          borderRadius="full"
                          minW="16px"
                          h="16px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          pointerEvents="none"
                        >
                          {item.qtd_anotacoes}
                        </Badge>
                      ) : null}
                    </Box>
                  </Tooltip>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );

  return (
    <PageBox>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-2">
        <div className="flex w-full content-start lg:w-1/2 lg:content-center">
          <ReturnButton previousPage="modulesHome/integration" />

          <div className="h-full">
            <div className="flex items-center justify-center rounded-full bg-[#E0F2FE] p-3">
              <FileText color="#0284C7" className="size-8" />
            </div>
          </div>
          <div className="flex w-full flex-col justify-center gap-1 pl-2">
            <Text fontWeight="bold" fontSize="16px">
              Análise Relatórios NFS-e
            </Text>
            <Text fontSize="12px" color="gray.500">
              Compare notas fiscais entre Relatório e Domínio
            </Text>
          </div>
        </div>
      </div>

      {/* Cards de Totalizadores */}
      {renderDashboardCards()}

      <FormBox>
        <div className="flex flex-col gap-3 p-3 sm:gap-4 sm:p-4">
          {/* Barra de Filtros e Toggle de Visão */}
          <div className="flex flex-col justify-between gap-2">
            <FormProvider {...filtersFormMethods}>
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                  {/* Campos de Pesquisa */}
                  <HStack spacing={2} flex={1} maxW={{ base: '100%', md: '500px' }}>
                    <SearchModeBar
                      modes={MODES_ID_NOME_CNPJ}
                      mode={searchMode}
                      onModeChange={m => setSearchMode(m as 'ID' | 'Nome' | 'CNPJ')}
                      value={filtersFormMethods.watch('search') ?? ''}
                      onValueChange={v =>
                        filtersFormMethods.setValue('search', v, { shouldDirty: true })
                      }
                    />
                  </HStack>
                </div>

                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {/* Toggle de Visão */}
                  <ButtonGroup size="sm" isAttached variant="outline">
                    <Tooltip label="Visão Detalhada (Tabela)">
                      <IconButton
                        aria-label="Visão Tabela"
                        icon={<ListBullets size={18} />}
                        onClick={() => setViewMode('table')}
                        colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
                        variant={viewMode === 'table' ? 'solid' : 'outline'}
                      />
                    </Tooltip>
                    <Tooltip label="Visão Simplificada (Grid)">
                      <IconButton
                        aria-label="Visão Grid"
                        icon={<SquaresFour size={18} />}
                        onClick={() => setViewMode('grid')}
                        colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
                        variant={viewMode === 'grid' ? 'solid' : 'outline'}
                      />
                    </Tooltip>
                  </ButtonGroup>

                  {/* Filtro de Data */}
                  <DateToolbarFilterImproved
                    setFilters={setFilters}
                    filters={filters}
                    showMonthYearOnly
                  />

                  {/* Botões de Filtro */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setIsFilterDrawerOpen(true)}
                      leftIcon={<FunnelSimple size={16} />}
                      variant="outline"
                      colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                      height="38px"
                    >
                      <Text display={{ base: 'none', xs: 'inline' }}>Filtros</Text>
                      {hasActiveFilters &&
                        ` (${Array.from(filters.keys()).filter(key => !['page', 'size', 'date_start', 'date_end', 'search', 'cnpj', 'external_id'].includes(key) && filters.get(key)).length})`}
                    </Button>

                    {/* Limpar Filtros */}
                    {hasActiveFilters && (
                      <Button
                        onClick={handleClearFilters}
                        leftIcon={<X size={16} />}
                        variant="ghost"
                        colorScheme="red"
                        height="38px"
                      >
                        <Text display={{ base: 'none', sm: 'inline' }}>Limpar</Text>
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* FilterDrawer para filtros avançados */}
              <FilterDrawer
                isOpen={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}
                title="Filtros Avançados"
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              >
                <form className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {/* Empresa */}
                  <SelectInputForm<ICompany>
                    name="empresa"
                    placeholder="Empresa"
                    closeMenuOnSelect
                    options={companies}
                    getOptionLabel={item => (item as ICompany).nome}
                    getOptionValue={item => String((item as ICompany).id)}
                    queryKeyName="CompaniesSelect"
                    getOptions={getCompanies}
                    isLoading={isFetchingCompanySelect}
                    controlMb={0}
                  />

                  {/* Status */}
                  <SelectInputForm<ISelect<string>>
                    name="status"
                    placeholder="Status Comparação"
                    options={STATUS_OPTIONS}
                    controlMb={0}
                  />

                  {/* Cidade */}
                  <SelectInputForm<ISelect<string>>
                    name="cidade"
                    placeholder="Cidade"
                    options={getFormattedLabelString(CompaniesFilters?.cidade)}
                    controlMb={0}
                  />

                  {/* UF */}
                  <SelectInputForm<ISelect<string>>
                    name="uf"
                    placeholder="UF"
                    options={getFormattedLabelString(CompaniesFilters?.uf)}
                    controlMb={0}
                  />

                  {/* Grupo de Empresas */}
                  <MultiSelectInputForm<ISelect<string>>
                    name="grupo_de_empresas"
                    placeholder="Selecione até 4 grupos"
                    options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
                    maxSelectedOptions={4}
                    controlMb={0}
                  />

                  {/* Fiscal */}
                  <SelectInputForm<ISelect<string>>
                    name="fiscal"
                    placeholder="Responsável Fiscal"
                    options={getFormattedLabelString(CompaniesFilters?.fiscal)}
                    controlMb={0}
                  />

                  {/* Contábil */}
                  <SelectInputForm<ISelect<string>>
                    name="contabil"
                    placeholder="Responsável Contábil"
                    options={getFormattedLabelString(CompaniesFilters?.contabil)}
                    controlMb={0}
                  />

                  {/* Tags */}
                  <SelectInputForm<ISelect<string>>
                    name="tags"
                    placeholder="Tags"
                    options={getFormattedLabelString(CompaniesFilters?.tags)}
                    controlMb={0}
                  />

                  {/* Comercial */}
                  <SelectInputForm<ISelect<string>>
                    name="comercial"
                    placeholder="Comercial"
                    options={getFormattedLabelString(CompaniesFilters?.comercial)}
                    controlMb={0}
                  />

                  {/* Financeiro */}
                  <SelectInputForm<ISelect<string>>
                    name="financeiro"
                    placeholder="Financeiro"
                    options={getFormattedLabelString(CompaniesFilters?.financeiro)}
                    controlMb={0}
                  />

                  {/* Contas a Pagar */}
                  <SelectInputForm<ISelect<string>>
                    name="contas_a_pagar"
                    placeholder="Contas a Pagar"
                    options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
                    controlMb={0}
                  />

                  {/* Encerramento Anual */}
                  <SelectInputForm<ISelect<string>>
                    name="encerramento_anual"
                    placeholder="Encerramento Anual"
                    options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
                    controlMb={0}
                  />

                  {/* Imposto de Renda */}
                  <SelectInputForm<ISelect<string>>
                    name="imposto_de_renda"
                    placeholder="Imposto de Renda"
                    options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
                    controlMb={0}
                  />

                  {/* Integração Contábil */}
                  <SelectInputForm<ISelect<string>>
                    name="integracao_contabil"
                    placeholder="Integração Contábil"
                    options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
                    controlMb={0}
                  />

                  {/* Integração Fiscal */}
                  <SelectInputForm<ISelect<string>>
                    name="integracao_fiscal"
                    placeholder="Integração Fiscal"
                    options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
                    controlMb={0}
                  />

                  {/* Pessoal */}
                  <SelectInputForm<ISelect<string>>
                    name="pessoal"
                    placeholder="Pessoal"
                    options={getFormattedLabelString(CompaniesFilters?.pessoal)}
                    controlMb={0}
                  />

                  {/* Relacionamento com Cliente */}
                  <SelectInputForm<ISelect<string>>
                    name="relacionamento_com_cliente"
                    placeholder="Relacionamento com Cliente"
                    options={getFormattedLabelString(CompaniesFilters?.relacionamento_com_cliente)}
                    controlMb={0}
                  />

                  {/* Societário */}
                  <SelectInputForm<ISelect<string>>
                    name="societario"
                    placeholder="Societário"
                    options={getFormattedLabelString(CompaniesFilters?.societario)}
                    controlMb={0}
                  />
                </form>
              </FilterDrawer>
            </FormProvider>
          </div>

          {/* Lista de Empresas */}
          {isFetchingRelatorioGeral && (
            <div className="flex flex-col items-center justify-center py-20">
              <Spinner size="xl" color="blue.500" />
              <Text mt={4} fontSize="lg" fontWeight="semibold">
                Carregando dados...
              </Text>
            </div>
          )}

          {!isFetchingRelatorioGeral && relatorioGeralList.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">
              <FileText size={48} color="#9CA3AF" />
              <Text fontSize="lg" color="gray.500" mt={4}>
                Nenhum dado encontrado.
              </Text>
              <Text fontSize="sm" color="gray.400">
                Tente ajustar os filtros ou o período selecionado.
              </Text>
            </div>
          )}

          {!isFetchingRelatorioGeral && relatorioGeralList.length > 0 && (
            <>
              {viewMode === 'table' ? renderTableView() : renderGridView()}

              {/* Paginação */}
              {pagination && (
                <div className="mt-4 flex flex-col gap-3 border-t border-gray-200 pt-4 sm:mt-6 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center justify-center gap-2 sm:justify-start">
                    <Text fontSize={{ base: 'xs', sm: 'sm' }} color="gray.600" textAlign="center">
                      <Text as="span" display={{ base: 'none', md: 'inline' }}>
                        Mostrando{' '}
                      </Text>
                      {(pagination.page - 1) * pagination.size + 1}-
                      {Math.min(pagination.page * pagination.size, pagination.total)} de{' '}
                      {pagination.total}
                    </Text>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        const newFilters = new URLSearchParams(Object.fromEntries(filters));
                        newFilters.set('page', String(pagination.page - 1));
                        setFilters(newFilters);
                      }}
                      isDisabled={pagination.page === 1}
                    >
                      <Text display={{ base: 'none', sm: 'inline' }}>Anterior</Text>
                      <Text display={{ base: 'inline', sm: 'none' }}>&lt;</Text>
                    </Button>
                    <Text
                      fontSize={{ base: 'xs', sm: 'sm' }}
                      fontWeight="semibold"
                      whiteSpace="nowrap"
                    >
                      {pagination.page}/{pagination.pages}
                    </Text>
                    <Button
                      size="sm"
                      onClick={() => {
                        const newFilters = new URLSearchParams(Object.fromEntries(filters));
                        newFilters.set('page', String(pagination.page + 1));
                        setFilters(newFilters);
                      }}
                      isDisabled={pagination.page === pagination.pages}
                    >
                      <Text display={{ base: 'none', sm: 'inline' }}>Próxima</Text>
                      <Text display={{ base: 'inline', sm: 'none' }}>&gt;</Text>
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </FormBox>

      {/* Modal para visualização de PDF */}
      <Modal isOpen={isPdfModalOpen} onClose={handleClosePdfModal} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{pdfModalTitle}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box display="flex" justifyContent="center">
              {pdfModalUrl ? (
                <iframe
                  src={pdfModalUrl}
                  style={{ width: '100%', height: '65vh' }}
                  frameBorder={0}
                  title={pdfModalTitle}
                />
              ) : (
                <Spinner size="xl" />
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleClosePdfModal}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageBox>
  );
}
